import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Container from "@components/Container/Container";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import { MetaTagDescriptor } from "@components/MetaTags/MetaTags";
import PulseEntry from "@features/pulse/components/PulseEntry/PulseEntry";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { Route } from "@route-types/Thrive/AboutUs/+types/ThriveAboutUsPage";
import ThriveNavigation from "@routes/Section/layouts/Thrive/components/ThriveNavigation";
import ThriveNewsletter from "@routes/Section/layouts/Thrive/components/ThriveNewsLetter";
import ThriveSocialMediaBanner from "@routes/Section/layouts/Thrive/components/ThriveSocialMediaBanner";
import ThriveTopBanner from "@routes/Section/layouts/Thrive/components/ThriveTopBanner";
import ThriveAboutUsDescription from "@routes/Thrive/AboutUs/components/ThriveAboutUsDescription";
import ThriveAboutUsTeamCards from "@routes/Thrive/AboutUs/components/ThriveAboutUsTeamCards/ThriveAboutUsTeamCards";
import { fetchThriveAboutUsPageData } from "@routes/Thrive/AboutUs/ThriveAboutUsPage.server";
import { THRIVE_ABOUT_US_GA_PROPS } from "@routes/Thrive/AboutUs/utils/contants";
import { ThriveAboutUsPageContext } from "@routes/Thrive/AboutUs/utils/types";
import ThriveGetSocial from "@routes/Thrive/components/ThriveGetSocial";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { sectionNavigationItems } from "@util/constant";

const ThriveAboutUsPage = (): React.ReactElement => {
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();

  useInjectClientData({ gaProps: THRIVE_ABOUT_US_GA_PROPS });

  return (
    <div className="w-full" data-testid="thrive-about-us-page-component">
      <Ad
        adType="lb1"
        className="flex min-h-[50px] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[250px]"
      />

      <div ref={breadcrumbsRef}></div>

      <Header
        displayBreadcrumb={isShowBreadCrumb}
        parentCategory={RouteFactory.thrive.replace("/", "")}
        childCategory={RouteFactory.thriveAboutUs.replace("/", "")}
        categoryDataMapping={sectionNavigationItems}
      />

      <PulseEntry />

      <Container>
        <ThriveTopBanner />

        <div className="mt-4 flex flex-col-reverse gap-6 lg:mt-8 lg:flex-row">
          <div className="w-full lg:w-8/12">
            <ThriveNavigation />

            <ThriveAboutUsDescription />

            <ThriveAboutUsTeamCards />

            <ThriveGetSocial />
          </div>

          <div className="border-b border-green-100 lg:mb-6 lg:mt-18 lg:block lg:w-4/12 lg:border-b-0">
            <ThriveSocialMediaBanner />

            <ThriveNewsletter />

            <div className="mb-6 mt-2 hidden lg:block lg:h-[800px]">
              <Ad
                adType="imu2"
                className="flex flex-col items-center justify-center lg:!sticky lg:top-18"
              />
            </div>
          </div>
        </div>
      </Container>

      <Ad adType="catfish" />
      <Ad adType="prestitial" />
    </div>
  );
};

export default ThriveAboutUsPage;

export const loader = async (): Promise<ThriveAboutUsPageContext> => {
  return await fetchThriveAboutUsPageData();
};

export const meta = ({ loaderData }: Route.MetaArgs): MetaTagDescriptor[] => {
  const context = loaderData as ThriveAboutUsPageContext;

  if (context.type !== ResponseType.SUCCESS) return [];

  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return data.metaTags;
};
